import SceneDirectionChoiceButton from 'js/editor/SceneDirectionChoiceButton';
import SceneDirectionMenu from 'js/editor/SceneDirectionMenu';
import { ReactComponent as IconArrowEast } from 'imgs/svg-icons/IconArrowEast.svg';
import { ReactComponent as IconArrowNorth } from 'imgs/svg-icons/IconArrowNorth.svg';
import { ReactComponent as IconArrowNorthEast } from 'imgs/svg-icons/IconArrowNorthEast.svg';
import { ReactComponent as IconArrowNorthWest } from 'imgs/svg-icons/IconArrowNorthWest.svg';
import { ReactComponent as IconArrowSouth } from 'imgs/svg-icons/IconArrowSouth.svg';
import { ReactComponent as IconArrowSouthEast } from 'imgs/svg-icons/IconArrowSouthEast.svg';
import { ReactComponent as IconArrowSouthWest } from 'imgs/svg-icons/IconArrowSouthWest.svg';
import { ReactComponent as IconArrowWest } from 'imgs/svg-icons/IconArrowWest.svg';
import { ReactComponent as IconNoAnimation } from 'imgs/svg-icons/IconNoAnimation.svg';
import { ReactComponent as IconSceneSlideIn } from 'imgs/svg-icons/IconSceneSlideIn.svg';
import { ReactComponent as IconSceneBounce } from 'imgs/svg-icons/IconSceneBounce.svg';
import { ReactComponent as IconSceneErase } from 'imgs/svg-icons/IconSceneErase.svg';
import { ReactComponent as IconSceneFade } from 'imgs/svg-icons/IconSceneFade.svg';
import { ReactComponent as IconSceneDrag } from 'imgs/svg-icons/IconSceneDrag.svg';
import SceneHandMenu from 'js/editor/SceneHandMenu';
import SceneCursorChoiceButton from 'js/editor/SceneCursorChoiceButton';
import { SceneChoiceButtonProps, SceneTransitionConfig, TransitionSubMenu, VSComponentProps } from 'js/types';

import {
  SCENE_TRANSITION_KEY_BOUNCE,
  SCENE_TRANSITION_KEY_FADE,
  SCENE_TRANSITION_KEY_NONE,
  SCENE_TRANSITION_KEY_SLIDE,
  SCENE_TRANSITION_KEY_ERASE,
  DEFAULT_ERASE_CURSOR_ID,
  SCENE_TRANSITION_KEY_DRAG_ON,
  DEFAULT_DRAG_CURSOR_ID
} from './consts';

export const DEFAULT_SCENE_TRANSITION_DURATION = 1;

export const SCENE_DIRECTION_KEYS = {
  UP: 'scene-direction-up',
  DOWN: 'scene-direction-down',
  LEFT: 'scene-direction-left',
  RIGHT: 'scene-direction-right',
  SCENE: 'scene-direction-scene',
  EMPTY: 'scene-direction-empty',
  UP_RIGHT: 'scene-direction-up-right',
  UP_LEFT: 'scene-direction-up-left',
  DOWN_RIGHT: 'scene-direction-down-right',
  DOWN_LEFT: 'scene-direction-down-left'
};

export const SCENE_DIRECTION_CHOICES = [
  { key: SCENE_DIRECTION_KEYS.EMPTY },
  { key: SCENE_DIRECTION_KEYS.DOWN, Icon: IconArrowSouth, title: 'Top' },
  { key: SCENE_DIRECTION_KEYS.EMPTY },
  { key: SCENE_DIRECTION_KEYS.RIGHT, Icon: IconArrowEast, title: 'Left' },
  { key: SCENE_DIRECTION_KEYS.EMPTY },
  { key: SCENE_DIRECTION_KEYS.LEFT, Icon: IconArrowWest, title: 'Right' },
  { key: SCENE_DIRECTION_KEYS.EMPTY },
  { key: SCENE_DIRECTION_KEYS.UP, Icon: IconArrowNorth, title: 'Bottom' },
  { key: SCENE_DIRECTION_KEYS.EMPTY }
];

export const ALL_SCENE_DIRECTION_CHOICES = [
  { key: SCENE_DIRECTION_KEYS.DOWN_RIGHT, Icon: IconArrowSouthEast, title: 'Top Left' },
  { key: SCENE_DIRECTION_KEYS.DOWN, Icon: IconArrowSouth, title: 'Top' },
  { key: SCENE_DIRECTION_KEYS.DOWN_LEFT, Icon: IconArrowSouthWest, title: 'Top Right' },
  { key: SCENE_DIRECTION_KEYS.RIGHT, Icon: IconArrowEast, title: 'Left' },
  { key: SCENE_DIRECTION_KEYS.EMPTY },
  { key: SCENE_DIRECTION_KEYS.LEFT, Icon: IconArrowWest, title: 'Right' },
  { key: SCENE_DIRECTION_KEYS.UP_RIGHT, Icon: IconArrowNorthEast, title: 'Bottom Left' },
  { key: SCENE_DIRECTION_KEYS.UP, Icon: IconArrowNorth, title: 'Bottom' },
  { key: SCENE_DIRECTION_KEYS.UP_LEFT, Icon: IconArrowNorthWest, title: 'Bottom Right' }
];

interface SceneTransitionOption {
  type: string;
  title: string;
  Icon: React.ComponentType;
  defaultConfig?: SceneTransitionConfig;
  secondaryMenu?: TransitionSubMenu<SceneChoiceButtonProps, VSComponentProps>;
  tertiaryMenu?: TransitionSubMenu<SceneChoiceButtonProps, VSComponentProps>;
}

const SCENE_TRANSITION_OPTIONS: Array<SceneTransitionOption> = [
  {
    type: SCENE_TRANSITION_KEY_NONE,
    title: 'None',
    Icon: IconNoAnimation
  },
  {
    type: SCENE_TRANSITION_KEY_FADE,
    title: 'Fade',
    Icon: IconSceneFade
  },
  {
    type: SCENE_TRANSITION_KEY_SLIDE,
    title: 'Slide In',
    Icon: IconSceneSlideIn,
    defaultConfig: {
      direction: SCENE_DIRECTION_KEYS.LEFT
    },
    secondaryMenu: {
      title: 'Direction',
      Component: SceneDirectionMenu,
      choiceButtonLabel: 'Direction',
      ChoiceButton: SceneDirectionChoiceButton
    }
  },
  {
    type: SCENE_TRANSITION_KEY_BOUNCE,
    title: 'Bounce',
    Icon: IconSceneBounce,
    defaultConfig: {
      direction: SCENE_DIRECTION_KEYS.DOWN
    },
    secondaryMenu: {
      title: 'Direction',
      Component: SceneDirectionMenu,
      choiceButtonLabel: 'Direction',
      ChoiceButton: SceneDirectionChoiceButton
    }
  },
  {
    type: SCENE_TRANSITION_KEY_ERASE,
    title: 'Erase',
    Icon: IconSceneErase,
    defaultConfig: {
      cursorId: DEFAULT_ERASE_CURSOR_ID
    },
    secondaryMenu: {
      title: 'Hand Type',
      Component: SceneHandMenu,
      choiceButtonLabel: 'Hand',
      ChoiceButton: SceneCursorChoiceButton
    }
  },
  {
    type: SCENE_TRANSITION_KEY_DRAG_ON,
    title: 'Drag on',
    Icon: IconSceneDrag,
    defaultConfig: {
      direction: SCENE_DIRECTION_KEYS.UP,
      cursorId: DEFAULT_DRAG_CURSOR_ID
    },
    secondaryMenu: {
      title: 'Direction',
      Component: SceneDirectionMenu,
      choiceButtonLabel: 'Direction',
      ChoiceButton: SceneDirectionChoiceButton,
      useAllDirections: true
    },
    tertiaryMenu: {
      title: 'Hand Type',
      Component: SceneHandMenu,
      choiceButtonLabel: 'Hand',
      ChoiceButton: SceneCursorChoiceButton
    }
  }
];

export default SCENE_TRANSITION_OPTIONS;
